// ============================================================================
// Site Service (Facade)
// Core site CRUD + re-exports from domain services for backward compatibility
// ============================================================================

import {
  Site,
  SiteCreate,
  SiteUpdate,
} from '../types/site';
import { fetchWithAuth } from './auth';

// Import services for the aggregated siteService object
import { siteConfigService } from './siteConfigService';
import { hookupService, deleteSiteHookup } from './hookupService';
import { pricingService } from './pricingService';
import { feeService } from './feeService';
import { blackoutService } from './blackoutService';
import { mediaService } from './mediaService';
import { siteAggregateService } from './siteAggregateService';
import {
  resolveEffectivePricing,
  resolveEffectiveOverageCharges,
  resolveEffectiveFees,
} from '../utils/effectiveValues';

// ============================================================================
// Re-export all domain services for backward compatibility
// ============================================================================

// Site Config (types + sections)
export {
  getStoreSiteTypes,
  createStoreSiteType,
  updateStoreSiteType,
  deleteStoreSiteType,
  getStoreSiteSections,
  createStoreSiteSection,
  updateStoreSiteSection,
  deleteStoreSiteSection,
  siteConfigService,
} from './siteConfigService';

// Hookups
export {
  getStoreHookupTypes,
  createStoreHookupType,
  updateStoreHookupType,
  deleteStoreHookupType,
  getSiteHookups,
  addSiteHookup,
  updateSiteHookup,
  deleteSiteHookup,
  hookupService,
} from './hookupService';

// Pricing
export {
  getSitePricing,
  createPricing,
  updatePricing,
  deletePricing,
  pricingService,
} from './pricingService';

// Fees and Charges
export {
  getSiteOverageFees,
  createSiteOverageFee,
  updateSiteOverageFee,
  deleteSiteOverageFee,
  getStoreOverageFees,
  createStoreOverageFee,
  updateStoreOverageFee,
  deleteStoreOverageFee,
  getSiteCharges,
  createSiteCharge,
  updateSiteCharge,
  deleteSiteCharge,
  getStoreCharges,
  feeService,
} from './feeService';

// Blackouts
export {
  getSiteBlackouts,
  createSiteBlackout,
  updateSiteBlackout,
  deleteSiteBlackout,
  getStoreBlackouts,
  createStoreBlackout,
  updateStoreBlackout,
  deleteStoreBlackout,
  blackoutService,
} from './blackoutService';

// Media
export {
  getSiteMedia,
  getMediaLimits,
  deleteSiteMedia,
  uploadSiteMedia,
  mediaService,
} from './mediaService';

// Aggregate/Composite Operations
export {
  getSiteWithDetails,
  siteAggregateService,
} from './siteAggregateService';

// ============================================================================
// Re-export effective value types and resolvers for backward compatibility
// ============================================================================

export type {
  EffectivePricingResult as EffectivePricing,
  EffectiveOverageChargeResult as EffectiveOverageCharge,
  EffectiveFeeResult as EffectiveFee,
} from '../utils/effectiveValues';

export {
  resolveEffectivePricing,
  resolveEffectiveOverageCharges,
  resolveEffectiveFees,
} from '../utils/effectiveValues';

// ============================================================================
// Core Site CRUD Operations
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

export async function getSites(storeId?: number): Promise<Site[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/sites/${queryString ? '?' + queryString : ''}`);
}

export async function getSite(siteId: number): Promise<Site> {
  return fetchWithAuth(`${API_BASE_URL}/sites/${siteId}`);
}

export async function createSite(site: SiteCreate, storeId?: number): Promise<Site> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/sites/${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
    body: JSON.stringify(site),
  });
}

export async function updateSite(siteId: number, site: SiteUpdate): Promise<Site> {
  return fetchWithAuth(`${API_BASE_URL}/sites/${siteId}`, {
    method: 'PUT',
    body: JSON.stringify(site),
  });
}

export async function deleteSite(siteId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/sites/${siteId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Named Export Object (aggregates all functions for backward compatibility)
// ============================================================================

export const siteService = {
  // Core Site CRUD
  getSites,
  getSite,
  createSite,
  updateSite,
  deleteSite,

  // Site Config
  ...siteConfigService,

  // Hookups (with alias)
  ...hookupService,
  removeSiteHookup: deleteSiteHookup,

  // Pricing
  ...pricingService,

  // Fees and Charges
  ...feeService,

  // Blackouts
  ...blackoutService,

  // Media
  ...mediaService,

  // Aggregate Operations
  ...siteAggregateService,

  // Effective Value Resolvers
  resolveEffectivePricing,
  resolveEffectiveOverageCharges,
  resolveEffectiveFees,
};

export default siteService;
